import Proptypes from 'prop-types'
import React, { createContext, useContext, useCallback, useRef, useEffect, useMemo } from 'react'

import { EditorContent, useEditor } from '@tiptap/react'
import { debounce } from 'lodash'

import { useResponsive, useUtilities } from '@/modules/cms/views/post/components/builder/context'

import defaultExtensions from '../extensions'

import BubbleToolbar from './BubbleToolbar'
import { enterKeyboard, tailwindBlock, tailwindParagraph, tableExtension, orderedListExtension, customParagraph, customList, customListItem, customOrderedList } from './extensions'
import { getTailwindValue } from './extensions/helpers'

export default function BubbleTiptap({ content, onChange, debounceMs = 2000 }) {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const debouncedOnChange = useMemo(() => debounce(html => onChangeRef.current(html), debounceMs), [debounceMs])
  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange])

  const handleChange = ({ editor }) => {
    const html = editor.getHTML()
    debouncedOnChange(html)
  }

  const handleChangeContent = ({ html, editor }) => {
    Promise.resolve().then(() => {
      debouncedOnChange(html)
      editor.commands.setContent(html, false)
    })
  }

  const editor = useEditor({
    extensions: [
      enterKeyboard,
      ...defaultExtensions.filter(extension => extension.name !== 'table'),
      customParagraph({ device, utilities: selectedUtilities }),
      customList,
      customListItem,
      customOrderedList,
      tailwindBlock({ device, utilities: selectedUtilities }),
      tailwindParagraph({ device, utilities: selectedUtilities }),
      orderedListExtension({ onChangeContent: handleChangeContent }),
      tableExtension,
    ],
    content,
    onUpdate: handleChange,
  })

  if (!editor) return null

  editor.getTailwindValue = props => getTailwindValue({ ...props, editor, device, utilities: selectedUtilities })

  return <BubbleTiptapProvider>
    <BubbleToolbar editor={editor} size={device === 'mobile' ? 'small' : 'default'} />
    <EditorContent editor={editor} />
  </BubbleTiptapProvider>
}

const BubbleTiptapContext = createContext({
  setBubblePopover: () => { },
  popover: { current: false },
  isBubbleContext: false,
})

const BubbleTiptapProvider = ({ children }) => {
  const popover = useRef(false)

  const setBubblePopover = useCallback(value => {
    popover.current = value
  }, [])

  return (
    <BubbleTiptapContext.Provider value={{ setBubblePopover, popover, isBubbleContext: true }}>
      {children}
    </BubbleTiptapContext.Provider>
  )
}

export const useBubbleTiptap = () => useContext(BubbleTiptapContext)

BubbleTiptap.propTypes = {
  editor: Proptypes.object,
}
